import React, { useEffect, useState } from "react";

import { clearLocalStorageFieldSortOption, getLocalStorageFieldSortOption, setLocalStorageFieldSortOption, useTranslation } from "utils";

import { Button, Flex, Icon, Popover, Typography } from "components";

export enum FIELD_SORT_OPTION {
  NAME_A_TO_Z = "nameAToZ",
  NAME_Z_TO_A = "nameZToA",
  SIZE_LARGEST_FIRST = "sizeLargestFirst",
  SIZE_SMALLEST_FIRST = "sizeSmallestFirst",
  NOT_ENROLLED_FIRST = "notEnrolledFirst",
}

export type FieldSortOptionData = {
  farmId: string;
  sortOption: FIELD_SORT_OPTION;
};

const SORT_SECTIONS: { titleKey: string; options: { value: FIELD_SORT_OPTION; labelKey: string }[] }[] = [
  {
    titleKey: "fieldSortOptions.sections.name.title",
    options: [
      { value: FIELD_SORT_OPTION.NAME_A_TO_Z, labelKey: "fieldSortOptions.sections.name.aToZ" },
      { value: FIELD_SORT_OPTION.NAME_Z_TO_A, labelKey: "fieldSortOptions.sections.name.zToA" },
    ],
  },
  {
    titleKey: "fieldSortOptions.sections.fieldSize.title",
    options: [
      { value: FIELD_SORT_OPTION.SIZE_LARGEST_FIRST, labelKey: "fieldSortOptions.sections.fieldSize.largestFirst" },
      { value: FIELD_SORT_OPTION.SIZE_SMALLEST_FIRST, labelKey: "fieldSortOptions.sections.fieldSize.smallestFirst" },
    ],
  },
  {
    titleKey: "fieldSortOptions.sections.enrollment.title",
    options: [{ value: FIELD_SORT_OPTION.NOT_ENROLLED_FIRST, labelKey: "fieldSortOptions.sections.enrollment.notEnrolledFirst" }],
  },
];

type SortOptionRowProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
  dataTestId?: string;
};

const SortOptionRow = ({ label, selected, onClick, dataTestId }: SortOptionRowProps) => (
  <Flex
    className={`filter-chip-option${selected ? " selected" : ""}`}
    justify="space-between"
    align="center"
    onClick={onClick}
    role="button"
    tabIndex={0}
    data-testid={dataTestId}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <Typography variant="paragraph-m" className="margin-0">
      {label}
    </Typography>
    {selected && <Icon iconName="tabler/circle-check" size="m-light" className="filter-chip-option-check" />}
  </Flex>
);

export const FieldSortOptions = ({
  appliedSortOption,
  setAppliedSortOption,
  farmId,
  isDisabled,
  isAutoEnrollmentType,
}: {
  appliedSortOption: FIELD_SORT_OPTION | null;
  setAppliedSortOption: (sortOption: FIELD_SORT_OPTION | null) => void;
  farmId: string;
  isDisabled: boolean;
  isAutoEnrollmentType: boolean;
}) => {
  const { t } = useTranslation("allFieldView");

  const [isOpen, setIsOpen] = useState(false);

  // fields with an auto enrollment type are always auto-enrolled, so the enrollment sort would be a no-op
  const visibleSortSections = isAutoEnrollmentType
    ? SORT_SECTIONS.filter((section) => section.titleKey !== "fieldSortOptions.sections.enrollment.title")
    : SORT_SECTIONS;

  // restore the saved sort option when the farm is known; reset when the farm has none saved (e.g. after a farm switch)
  useEffect(() => {
    if (!farmId) return;

    const savedSortOption = getLocalStorageFieldSortOption(farmId);
    const isValidSortOption =
      !!savedSortOption &&
      Object.values(FIELD_SORT_OPTION).includes(savedSortOption) &&
      !(isAutoEnrollmentType && savedSortOption === FIELD_SORT_OPTION.NOT_ENROLLED_FIRST);
    setAppliedSortOption(isValidSortOption ? savedSortOption : null);
  }, [farmId, isAutoEnrollmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSortOption = (sortOption: FIELD_SORT_OPTION) => {
    setAppliedSortOption(sortOption);
    setLocalStorageFieldSortOption({ farmId, sortOption });
    setIsOpen(false);
  };

  const handleResetToDefault = () => {
    setAppliedSortOption(null);
    clearLocalStorageFieldSortOption();
    setIsOpen(false);
  };

  return (
    <Flex className={`field-sort-options${appliedSortOption ? " active" : ""}${isDisabled ? " disabled" : ""}`}>
      <Popover
        open={isOpen}
        onOpenChange={(open: boolean) => {
          if (!isDisabled) setIsOpen(open);
        }}
        trigger="click"
        placement="bottomLeft"
        className="filter-chip-popover"
        content={
          <Flex vertical className="filter-chip-popover-content field-sort-options-popover-content" data-testid="field-sort-options-container">
            {visibleSortSections.map((section) => (
              <React.Fragment key={section.titleKey}>
                <Typography variant="paragraph-m" color="grey-600" className="field-sort-options-section-title margin-0">
                  {t(section.titleKey)}
                </Typography>
                {section.options.map((option) => (
                  <SortOptionRow
                    key={option.value}
                    label={t(option.labelKey)}
                    selected={appliedSortOption === option.value}
                    onClick={() => handleSelectSortOption(option.value)}
                    dataTestId={`field-sort-option-${option.value}`}
                  />
                ))}
              </React.Fragment>
            ))}
            {appliedSortOption && (
              <Button type="text" className="field-sort-options-reset" onClick={handleResetToDefault} data-testid="field-sort-options-reset">
                {t("fieldSortOptions.resetToDefault")}
              </Button>
            )}
          </Flex>
        }
      >
        <Flex className="field-sort-options-trigger" align="center" gap="0.5rem" role="button" tabIndex={0} data-testid="field-sort-options-trigger">
          <Icon iconName="tabler/arrows-sort" size="m-light" />
          {appliedSortOption && <span className="field-sort-options-active-dot" data-testid="field-sort-options-active-dot" />}
          <Icon iconName="tabler/chevron-down" size="s" />
        </Flex>
      </Popover>
    </Flex>
  );
};
